/**
 * Deterministic team name generator seeded from matchId.
 *
 * Uses a simple hash + LCG PRNG to produce repeatable,
 * unique team names for any given match.
 */

const TEAM_NAME_POOL = [
  "Phantom",
  "Vortex",
  "Reaper",
  "Ironclad",
  "Eclipse",
  "Spectre",
  "Titan",
  "Havoc",
  "Zenith",
  "Onslaught",
  "Raptor",
  "Valkyrie",
  "Tempest",
  "Colossus",
  "Frostbite",
  "Inferno",
  "Basilisk",
  "Striker",
  "Nomad",
  "Vanguard",
  "Cyclone",
  "Obsidian",
  "Wraith",
  "Juggernaut",
  "Catalyst",
  "Dominus",
  "Harbinger",
  "Nexus",
  "Predator",
  "Seraph",
  "Renegade",
  "Apex",
  "Meridian",
  "Warden",
  "Ronin",
  "Paragon",
  "Ember",
  "Sentinel",
  "Cipher",
  "Dagger",
] as const;

/**
 * Simple deterministic hash: string -> 32-bit integer.
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash + chr) | 0;
  }
  return hash >>> 0; // unsigned
}

/**
 * LCG PRNG returning floats in [0, 1).
 * Returns a next() function that advances the generator.
 */
function seededRandom(seed: number) {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) | 0;
    return (state >>> 0) / 0x100000000;
  };
}

/**
 * Get `count` unique team names deterministically seeded from `matchId`.
 * Uses a partial Fisher-Yates shuffle to pick names without repeats.
 */
export function getTeamNames(matchId: string, count: number): string[] {
  const seed = hashString(matchId);
  const rng = seededRandom(seed);
  const pool = [...TEAM_NAME_POOL];

  const n = Math.min(count, pool.length);
  const result: string[] = [];

  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
    result.push(pool[i]);
  }

  return result;
}
